import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import useFormValidation from "hooks/useFormValidation";
import { projectValidationRules } from "constants/validationRules";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  TextField,
  Button,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
  useTheme,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  LinearProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  Add as AddIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { createNewProject } from "store/features/company/projectManagement/project/projectActions";
import { fetchDepartments } from "store/features/company/department/departmentActions";
import {
  selectDepartments,
  selectDepartmentLoading,
} from "store/features/company/department/departmentSelectors";
import {
  fetchEmployees,
  fetchEmployeeWorkloads,
} from "store/features/company/employee/employeeActions";
import {
  selectEmployees,
  selectEmployeeLoading,
  selectEmployeeWorkloads,
  selectEmployeeWorkloadsLoading,
} from "store/features/company/employee/employeeSelectors";
import { selectCurrentUser } from "store/features/auth/authSelectors";
import { getSecureLocalItem } from "utils/secureSessionStorage";
import addProjectSvg from "assets/modals/add.svg";
import { getEmployeeWorkload } from "services/company/projectManagement/project/projectService";

const getProjectDateError = ({ start_date, end_date }) => {
  if (start_date && end_date && start_date > end_date) {
    return "Start date cannot be after end date";
  }

  return "";
};

const AddProjectModal = ({ isOpen, onClose, onSuccess }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const { errors, validateForm, validateField, resetErrors } =
    useFormValidation(projectValidationRules);

  const currentUser = useSelector(selectCurrentUser);
  const userRaw = getSecureLocalItem("user");
  const storedUser = userRaw
    ? typeof userRaw === "string"
      ? JSON.parse(userRaw)
      : userRaw
    : null;
  const user = currentUser || storedUser;

  const departments = useSelector(selectDepartments);
  const departmentsLoading = useSelector(selectDepartmentLoading);
  const employees = useSelector(selectEmployees);
  const employeesLoading = useSelector(selectEmployeeLoading);

  const currentEmployee = employees.find(
    (emp) =>
      (user?.employee_id && String(emp.id) === String(user.employee_id)) ||
      (user?.id && String(emp.user_id) === String(user.id)) ||
      (user?.id && String(emp.id) === String(user.id)) ||
      (user?.email &&
        emp.email &&
        emp.email.toLowerCase() === user.email.toLowerCase()),
  );
  const currentEmployeeId = currentEmployee?.id || user?.employee_id;

  const [formData, setFormData] = useState({
    name: "",
    code: "",
    description: "",
    department_id: "",
    project_manager_id: "",
    start_date: "",
    end_date: "",
    budget: "",
    status: "active",
    priority: "medium",
    assignment_type: "Meeting",
    tags: [],
    team_members: [],
  });
  const [tagInput, setTagInput] = useState("");
  const [teamMembersInput, setTeamMembersInput] = useState("");
  const workloads = useSelector(selectEmployeeWorkloads);
  const workloadsLoading = useSelector(selectEmployeeWorkloadsLoading);
  const dateError = getProjectDateError(formData);

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchDepartments());
      dispatch(fetchEmployees());
      dispatch(fetchEmployeeWorkloads());
    }
  }, [isOpen, dispatch]);

  // Auto-add current employee into team members when employee is resolved
  useEffect(() => {
    if (
      currentEmployeeId &&
      !formData.team_members?.includes(parseInt(currentEmployeeId))
    ) {
      setFormData((prev) => ({
        ...prev,
        team_members: [
          ...(prev.team_members || []),
          parseInt(currentEmployeeId),
        ],
      }));
    }
  }, [currentEmployeeId]);

  //for single scrollbar when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const handleAddTeamMember = () => {
    if (
      teamMembersInput &&
      !formData.team_members?.includes(parseInt(teamMembersInput))
    ) {
      setFormData((prev) => ({
        ...prev,
        team_members: [
          ...(prev.team_members || []),
          parseInt(teamMembersInput),
        ],
      }));
      setTeamMembersInput("");
    }
  };
  //team lead into the team
  useEffect(() => {
    if (
      formData.project_manager_id &&
      !formData.team_members?.includes(parseInt(formData.project_manager_id))
    ) {
      setFormData((prev) => ({
        ...prev,
        team_members: [
          ...(prev.team_members || []),
          parseInt(prev.project_manager_id),
        ],
      }));
    }
  }, [formData.project_manager_id]);

  const handleRemoveTeamMember = (memberId) => {
    setFormData((prev) => ({
      ...prev,
      team_members: prev.team_members?.filter((id) => id !== memberId) || [],
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const nextFormData = { ...prev, [name]: value };
      validateField(name, value, nextFormData);
      return nextFormData;
    });
  };

  const handleDateChange = (name, value) => {
    const formattedValue =
      value && dayjs(value).isValid() ? dayjs(value).format("YYYY-MM-DD") : "";
    setFormData((prev) => {
      const nextFormData = { ...prev, [name]: formattedValue };
      validateField(name, formattedValue, nextFormData);
      return nextFormData;
    });
  };

  const handleBudgetChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setFormData((prev) => {
        const nextFormData = { ...prev, budget: value };
        validateField("budget", value, nextFormData);
        return nextFormData;
      });
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm(formData)) {
      return;
    }
    if (dateError) {
      return;
    }

    setLoading(true);
    const submitData = {
      name: formData.name,
      code: formData.code,
      description: formData.description,
      department_id: formData.department_id
        ? parseInt(formData.department_id)
        : undefined,
      project_manager_id: formData.project_manager_id
        ? parseInt(formData.project_manager_id)
        : undefined,
      team_members: formData.team_members || [],
      start_date: formData.start_date,
      end_date: formData.end_date,
      budget: formData.budget ? parseFloat(formData.budget) : 0,
      status: formData.status,
      priority: formData.priority,
      assignment_type: formData.assignment_type,

      tags: formData.tags,
    };

    const result = await dispatch(createNewProject(submitData));

    if (result.success) {
      onSuccess();
      onClose();
      resetForm();
    }
    setLoading(false);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      code: "",
      description: "",
      department_id: "",
      project_manager_id: "",
      start_date: "",
      end_date: "",
      budget: "",
      status: "active",
      priority: "medium",
      assignment_type: "Meeting",

      tags: [],
      team_members: currentEmployeeId ? [parseInt(currentEmployeeId)] : [],
    });
    setTagInput("");
    setTeamMembersInput("");
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
      "& fieldset": {
        borderColor: theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
      },
      "&:hover fieldset": {
        borderColor: "#f59e0b",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#f59e0b",
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      color: "text.secondary",
      position: "relative",
      transform: "none",
      marginBottom: "8px",
      display: "block",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "primary.main",
    },
    "& .MuiInputLabel-asterisk": {
      color: "#ef4444",
    },
  };
  const selectSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
      "& fieldset": {
        borderColor: theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
      },
      "&:hover fieldset": {
        borderColor: "#f59e0b",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#f59e0b",
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      color: "text.secondary",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "primary.main",
    },
  };
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <style>
            {`
              .MuiPopover-root { z-index: 1400 !important; }
              .MuiPopper-root { z-index: 1400 !important; }
              .MuiMenu-root { z-index: 1400 !important; }
            `}
          </style>
          {/* Backdrop with fade animation */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1300,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(252, 226, 181, 0.14)"
                  : "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(40px)",
            }}
          />

          {/* Modal with slide-in from right animation */}
          <motion.div
            initial={{ x: "100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            style={{
              position: "fixed",
              right: 0,
              top: 0,
              zIndex: 1301,
              height: "100%",
              width: "100%",
              maxWidth: "1000px",
              overflowY: "auto",
              backgroundColor:
                theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              boxShadow: theme.shadows[24],
            }}
          >
            {/* Header */}
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                borderBottom: `1px solid ${
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                }`,
                backgroundColor:
                  theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
                position: "relative",
              }}
            >
              <Box sx={{ p: 3, pr: 7 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box flexShrink={0}>
                    <Box
                      component="img"
                      src={addProjectSvg}
                      alt="Add Project"
                      sx={{
                        width: 80,
                        height: 80,
                        objectFit: "contain",
                        transition: "all 0.3s",
                        "&:hover": {
                          filter: "drop-shadow(0 4px 8px rgba(245,158,11,0.3))",
                        },
                      }}
                    />
                  </Box>
                  <Box flex={1}>
                    <Typography
                      variant="h5"
                      component="h2"
                      sx={{
                        fontWeight: "bold",
                        color: "text.primary",
                      }}
                    >
                      Add Project
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        color: "text.secondary",
                      }}
                    >
                      Add a new project to your organization
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <IconButton
                onClick={onClose}
                sx={{
                  position: "absolute",
                  top: 20,
                  right: 20,
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  color: "text.secondary",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#f59e0b",
                    color: "#ffffff",
                    transform: "scale(1.05)",
                  },
                  "&:active": {
                    transform: "scale(0.95)",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <Box sx={{ p: 3 }}>
                <Stack spacing={2.5}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      fullWidth
                      label="Project Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      error={!!errors.name}
                      helperText={errors.name}
                      required
                      placeholder="Enter project name"
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                    />

                    <TextField
                      fullWidth
                      label="Project Code"
                      name="code"
                      value={formData.code}
                      onChange={handleChange}
                      required
                      placeholder="e.g., PRJ001"
                      error={!!errors.code}
                      helperText={errors.code || "Unique code for the project"}
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Stack>

                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    placeholder="Brief description of the project"
                    sx={inputSx}
                    InputLabelProps={{ shrink: true }}
                  />

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <Autocomplete
                      fullWidth
                      options={departments}
                      getOptionLabel={(option) => option.name || ""}
                      value={
                        departments.find(
                          (d) => d.id === formData.department_id,
                        ) || null
                      }
                      onChange={(e, newValue) => {
                        setFormData((prev) => ({
                          ...prev,
                          department_id: newValue ? newValue.id : "",
                        }));
                      }}
                      loading={departmentsLoading}
                      disabled={departmentsLoading}
                      filterOptions={(options, state) => {
                        const inputValue = state.inputValue.toLowerCase();
                        return options.filter(
                          (option) =>
                            option.name?.toLowerCase().includes(inputValue) ||
                            option.code?.toLowerCase().includes(inputValue),
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Department"
                          placeholder="Search by name or code"
                          sx={inputSx}
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                      renderOption={(props, option) => {
                        const { key, ...otherProps } = props;
                        return (
                          <Box component="li" key={key} {...otherProps}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {option.name}
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                sx={{ ml: 1 }}
                              >
                                ({option.code})
                              </Typography>
                            </Typography>
                          </Box>
                        );
                      }}
                    />

                    <Autocomplete
                      fullWidth
                      options={workloads}
                      getOptionLabel={(option) => option.name || ""}
                      value={
                        workloads.find(
                          (w) => w.employee_id === formData.project_manager_id,
                        ) || null
                      }
                      onChange={(e, newValue) => {
                        setFormData((prev) => ({
                          ...prev,
                          project_manager_id: newValue
                            ? newValue.employee_id
                            : "",
                        }));
                      }}
                      loading={workloadsLoading}
                      disabled={workloadsLoading}
                      filterOptions={(options, state) => {
                        const inputValue = state.inputValue.toLowerCase();
                        return options.filter(
                          (option) =>
                            option.name?.toLowerCase().includes(inputValue) ||
                            option.role?.toLowerCase().includes(inputValue),
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Project Lead"
                          placeholder="Search by name or role"
                          sx={inputSx}
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                      renderOption={(props, option) => {
                        const { key, ...otherProps } = props;
                        const colorMap = {
                          high: "error",
                          medium: "warning",
                          low: "success",
                        };
                        const status =
                          option.workload_status?.toLowerCase() || "low";
                        const color = colorMap[status] || "primary";
                        const percentage =
                          parseFloat(option.workload_percentage) || 0;

                        return (
                          <Box
                            component="li"
                            key={key}
                            {...otherProps}
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              width: "100%",
                              py: 1,
                              borderBottom: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                width: "100%",
                                mb: 0.5,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {option.name}
                              </Typography>
                              <Chip
                                label={
                                  option.role
                                    ? option.role.replace(/_/g, " ")
                                    : ""
                                }
                                size="small"
                                sx={{ height: 20, fontSize: "0.7rem" }}
                              />
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                width: "100%",
                                gap: 1,
                              }}
                            >
                              <Box sx={{ flexGrow: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.min(percentage, 100)}
                                  color={color}
                                  sx={{ height: 6, borderRadius: 3 }}
                                />
                              </Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ minWidth: 40, textAlign: "right" }}
                              >
                                {percentage.toFixed(1)}%
                              </Typography>
                            </Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mt: 0.5, alignSelf: "start" }}
                            >
                              Active Projects: {option.active_projects} | Tasks:{" "}
                              {option.active_tasks}
                            </Typography>
                          </Box>
                        );
                      }}
                    />
                  </Stack>

                  {/* Team Members Section */}
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1,
                        color: "text.secondary",
                        fontWeight: 500,
                      }}
                    >
                      Team Members
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Autocomplete
                        fullWidth
                        size="small"
                        options={workloads.filter(
                          (w) =>
                            !formData.team_members?.includes(w.employee_id),
                        )}
                        getOptionLabel={(option) => option.name || ""}
                        value={
                          workloads.find(
                            (w) => w.employee_id === parseInt(teamMembersInput),
                          ) || null
                        }
                        onChange={(e, newValue) => {
                          setTeamMembersInput(
                            newValue ? newValue.employee_id.toString() : "",
                          );
                        }}
                        loading={workloadsLoading}
                        disabled={workloadsLoading}
                        filterOptions={(options, state) => {
                          const inputValue = state.inputValue.toLowerCase();
                          return options.filter(
                            (option) =>
                              option.name?.toLowerCase().includes(inputValue) ||
                              option.role?.toLowerCase().includes(inputValue),
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Team Member"
                            placeholder="Search by name or role"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "12px",
                                backgroundColor:
                                  theme.palette.mode === "dark"
                                    ? "#1e293b"
                                    : "#f9fafb",
                              },
                            }}
                          />
                        )}
                        renderOption={(props, option) => {
                          const { key, ...otherProps } = props;
                          const colorMap = {
                            high: "error",
                            medium: "warning",
                            low: "success",
                          };
                          const status =
                            option.workload_status?.toLowerCase() || "low";
                          const color = colorMap[status] || "primary";
                          const percentage =
                            parseFloat(option.workload_percentage) || 0;

                          return (
                            <Box
                              component="li"
                              key={key}
                              {...otherProps}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                width: "100%",
                                py: 1,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  width: "100%",
                                  mb: 0.5,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {option.name}
                                </Typography>
                                <Chip
                                  label={
                                    option.role
                                      ? option.role.replace(/_/g, " ")
                                      : ""
                                  }
                                  size="small"
                                  sx={{ height: 20, fontSize: "0.7rem" }}
                                />
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  width: "100%",
                                  gap: 1,
                                }}
                              >
                                <Box sx={{ flexGrow: 1 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={Math.min(percentage, 100)}
                                    color={color}
                                    sx={{ height: 6, borderRadius: 3 }}
                                  />
                                </Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ minWidth: 40, textAlign: "right" }}
                                >
                                  {percentage.toFixed(1)}%
                                </Typography>
                              </Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 0.5 }}
                              >
                                Active Projects: {option.active_projects} |
                                Tasks: {option.active_tasks}
                              </Typography>
                            </Box>
                          );
                        }}
                      />
                      <Button
                        onClick={handleAddTeamMember}
                        variant="outlined"
                        sx={{
                          borderRadius: "12px",
                          borderColor: "#f59e0b",
                          color: "primary.main",
                          "&:hover": {
                            borderColor: "#d97706",
                            backgroundColor: "rgba(245,158,11,0.1)",
                          },
                        }}
                      >
                        <AddIcon />
                      </Button>
                    </Stack>
                    {formData.team_members &&
                      formData.team_members.length > 0 && (
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}
                        >
                          {formData.team_members.map((memberId, index) => {
                            const member = employees.find(
                              (emp) => emp.id === memberId,
                            );
                            return (
                              <Chip
                                key={index}
                                label={
                                  member
                                    ? `${member.first_name} ${member.last_name}`
                                    : `Employee ${memberId}`
                                }
                                onDelete={() =>
                                  handleRemoveTeamMember(memberId)
                                }
                                size="small"
                                icon={<PersonIcon sx={{ fontSize: 14 }} />}
                                sx={{
                                  backgroundColor:
                                    theme.palette.mode === "dark"
                                      ? "#334155"
                                      : "#fef3c7",
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#d1d5db"
                                      : "#92400e",
                                  "& .MuiChip-deleteIcon": {
                                    color: "primary.main",
                                  },
                                  mb: 0.5,
                                }}
                              />
                            );
                          })}
                        </Stack>
                      )}
                  </Box>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <DatePicker
                      label="Start Date"
                      value={
                        formData.start_date ? dayjs(formData.start_date) : null
                      }
                      onChange={(newValue) =>
                        handleDateChange("start_date", newValue)
                      }
                      maxDate={
                        formData.end_date ? dayjs(formData.end_date) : undefined
                      }
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          error: Boolean(dateError),
                          helperText: dateError,
                          sx: inputSx,
                          InputLabelProps: { shrink: true },
                        },
                      }}
                    />

                    <DatePicker
                      label="End Date"
                      value={
                        formData.end_date ? dayjs(formData.end_date) : null
                      }
                      onChange={(newValue) =>
                        handleDateChange("end_date", newValue)
                      }
                      minDate={
                        formData.start_date
                          ? dayjs(formData.start_date)
                          : undefined
                      }
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          error: Boolean(dateError),
                          helperText: dateError,
                          sx: inputSx,
                          InputLabelProps: { shrink: true },
                        },
                      }}
                    />
                  </Stack>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      fullWidth
                      label="Budget (₹)"
                      name="budget"
                      value={formData.budget}
                      onChange={handleBudgetChange}
                      placeholder="500000"
                      error={Boolean(errors.budget)}
                      helperText={errors.budget || "Project budget in INR"}
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                    />

                    <TextField
                      fullWidth
                      select
                      label="Status"
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                    >
                      <MenuItem value="planning">Planning</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="on_hold">On Hold</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </TextField>

                    <TextField
                      fullWidth
                      select
                      label="Priority"
                      name="priority"
                      value={formData.priority}
                      onChange={handleChange}
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                    >
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="low">Low</MenuItem>
                      <MenuItem value="urgent">Urgent</MenuItem>
                    </TextField>

                    <TextField
                      fullWidth
                      select
                      label="Assignment Type"
                      name="assignment_type"
                      value={formData.assignment_type}
                      onChange={handleChange}
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                    >
                      <MenuItem value="Email">Email</MenuItem>
                      <MenuItem value="Written">Written</MenuItem>
                      <MenuItem value="Verbal">Verbal</MenuItem>
                      <MenuItem value="Meeting Resolutions">
                        Meeting Resolutions
                      </MenuItem>
                      <MenuItem value="Regular Assignments">
                        Regular Assignments
                      </MenuItem>
                      <MenuItem value="Self">Self</MenuItem>
                    </TextField>
                  </Stack>

                  {/* Tags Section */}
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1,
                        color: "text.secondary",
                        fontWeight: 500,
                      }}
                    >
                      Tags
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        fullWidth
                        placeholder="Add tags... (press Enter or comma to add)"
                        value={tagInput}
                        onChange={(e) => {
                          const value = e.target.value;
                          setTagInput(value);
                          // Auto-add tag when comma is typed
                          if (value.endsWith(",")) {
                            const newTag = value.slice(0, -1).trim();
                            if (newTag && !formData.tags.includes(newTag)) {
                              setFormData((prev) => ({
                                ...prev,
                                tags: [...prev.tags, newTag],
                              }));
                            }
                            setTagInput("");
                          }
                        }}
                        onKeyPress={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            if (
                              tagInput.trim() &&
                              !formData.tags.includes(tagInput.trim())
                            ) {
                              setFormData((prev) => ({
                                ...prev,
                                tags: [...prev.tags, tagInput.trim()],
                              }));
                              setTagInput("");
                            }
                          }
                        }}
                        onBlur={() => {
                          // Add tag when focus leaves the input
                          if (
                            tagInput.trim() &&
                            !formData.tags.includes(tagInput.trim())
                          ) {
                            setFormData((prev) => ({
                              ...prev,
                              tags: [...prev.tags, tagInput.trim()],
                            }));
                            setTagInput("");
                          }
                        }}
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "#1e293b"
                                : "#f9fafb",
                          },
                        }}
                      />
                      <Button
                        onClick={handleAddTag}
                        variant="outlined"
                        sx={{
                          borderRadius: "12px",
                          borderColor: "#f59e0b",
                          color: "primary.main",
                          "&:hover": {
                            borderColor: "#d97706",
                            backgroundColor: "rgba(245,158,11,0.1)",
                          },
                        }}
                      >
                        <AddIcon />
                      </Button>
                    </Stack>
                    {formData.tags.length > 0 && (
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ mt: 1 }}
                        flexWrap="wrap"
                      >
                        {formData.tags.map((tag, index) => (
                          <Chip
                            key={index}
                            label={tag}
                            onDelete={() => handleRemoveTag(tag)}
                            size="small"
                            sx={{
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "#334155"
                                  : "#f3f4f6",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#d1d5db"
                                  : "#374151",
                              "& .MuiChip-deleteIcon": {
                                color: "primary.main",
                              },
                            }}
                          />
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </Box>

              <Box
                sx={{
                  p: 3,
                  borderTop: `1px solid ${
                    theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                  }`,
                  display: "flex",
                  gap: 2,
                }}
              >
                <Button
                  onClick={onClose}
                  disabled={loading}
                  fullWidth
                  sx={{
                    borderRadius: "12px",
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 500,
                    borderColor:
                      theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                    color:
                      theme.palette.mode === "dark" ? "#d1d5db" : "#374151",
                    "&:hover": {
                      borderColor: "#f59e0b",
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(245,158,11,0.1)"
                          : "#fffbeb",
                    },
                  }}
                  variant="outlined"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    loading ||
                    !formData.name.trim() ||
                    !formData.code.trim() ||
                    Boolean(dateError)
                  }
                  fullWidth
                  sx={{
                    borderRadius: "12px",
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 500,
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    color: "#ffffff",
                    boxShadow: "0 10px 15px -3px rgba(245, 158, 11, 0.25)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                      transform: "scale(1.02)",
                      boxShadow: "0 20px 25px -5px rgba(245, 158, 11, 0.3)",
                    },
                    "&:disabled": {
                      opacity: 0.7,
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "#ffffff" }} />
                  ) : (
                    "Add Project"
                  )}
                </Button>
              </Box>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AddProjectModal;
